import React, { useState, useEffect, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Save, Home, Trash2, ExternalLink } from "lucide-react";
import { useAdmin } from "../../context/AdminContext";
import { useContent } from "../../context/ContentContext";
import { api, resolveImage } from "../../lib/api";
import { toast } from "sonner";
import { Field, ListField, ImageField, inputCls, labelCls } from "../../components/admin/Fields";

const TABS = [
  { id: "home", label: "Home" },
  { id: "details", label: "Wedding Details" },
  { id: "journey", label: "Our Journey" },
  { id: "family", label: "Our Family" },
  { id: "rsvp", label: "RSVP Page" },
  { id: "nav", label: "Navigation" },
  { id: "settings", label: "Settings" },
  { id: "photos", label: "Photos" },
  { id: "submissions", label: "RSVP List" },
];

const SaveBar = ({ onSave, saving }) => (
  <div className="sticky bottom-0 -mx-6 md:-mx-10 px-6 md:px-10 py-4 bg-white/90 backdrop-blur border-t border-border mt-10 flex justify-end">
    <button
      onClick={onSave}
      disabled={saving}
      className="inline-flex items-center gap-2 rounded-full bg-navy text-white uppercase tracking-[0.16em] text-xs px-7 py-3.5 hover:bg-dusty transition-colors disabled:opacity-60"
      data-testid="save-section-btn"
    >
      <Save size={15} /> {saving ? "Saving..." : "Save changes"}
    </button>
  </div>
);

const useSection = (key, content, refresh) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (content && content[key]) setDraft(JSON.parse(JSON.stringify(content[key])));
  }, [content, key]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/content/${key}`, { data: draft });
      await refresh();
      toast.success("Saved successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return { draft, set, save, saving };
};

const HomeEditor = ({ content, refresh }) => {
  const { draft, set, save, saving } = useSection("home", content, refresh);
  if (!draft) return null;
  return (
    <div className="space-y-6">
      <ImageField label="Hero Image" value={draft.heroImageUrl} category="hero" onChange={(v) => set("heroImageUrl", v)} />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field field={{ label: "Kicker", type: "text" }} value={draft.kicker} onChange={(v) => set("kicker", v)} />
        <Field field={{ label: "Ampersand / Joiner", type: "text" }} value={draft.amp} onChange={(v) => set("amp", v)} />
        <Field field={{ label: "Name Line 1", type: "text" }} value={draft.nameLine1} onChange={(v) => set("nameLine1", v)} />
        <Field field={{ label: "Name Line 2", type: "text" }} value={draft.nameLine2} onChange={(v) => set("nameLine2", v)} />
        <Field field={{ label: "Date", type: "text" }} value={draft.date} onChange={(v) => set("date", v)} />
        <Field field={{ label: "Time", type: "text" }} value={draft.time} onChange={(v) => set("time", v)} />
        <Field field={{ label: "Location", type: "text" }} value={draft.location} onChange={(v) => set("location", v)} />
        <Field field={{ label: "More Details Label", type: "text" }} value={draft.moreDetailsLabel} onChange={(v) => set("moreDetailsLabel", v)} />
      </div>
      <Field field={{ label: "Intro Text", type: "rich" }} value={draft.introHtml} onChange={(v) => set("introHtml", v)} />
      <SaveBar onSave={save} saving={saving} />
    </div>
  );
};

const DetailsEditor = ({ content, refresh }) => {
  const { draft, set, save, saving } = useSection("details", content, refresh);
  if (!draft) return null;
  return (
    <div className="space-y-6">
      <ImageField label="Venue Hero Image" value={draft.heroImageUrl} category="hero" onChange={(v) => set("heroImageUrl", v)} />
      <Field field={{ label: "Section Title", type: "text" }} value={draft.sectionTitle} onChange={(v) => set("sectionTitle", v)} />
      <Field field={{ label: "Body Text", type: "rich" }} value={draft.bodyHtml} onChange={(v) => set("bodyHtml", v)} />
      <ImageField label="Side Image" value={draft.sideImageUrl} category="details" onChange={(v) => set("sideImageUrl", v)} />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field field={{ label: "Ceremony Title", type: "text" }} value={draft.ceremonyTitle} onChange={(v) => set("ceremonyTitle", v)} />
        <Field field={{ label: "Reception Title", type: "text" }} value={draft.receptionTitle} onChange={(v) => set("receptionTitle", v)} />
      </div>
      <Field field={{ label: "Ceremony Details", type: "rich" }} value={draft.ceremonyHtml} onChange={(v) => set("ceremonyHtml", v)} />
      <Field field={{ label: "Reception Details", type: "rich" }} value={draft.receptionHtml} onChange={(v) => set("receptionHtml", v)} />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field field={{ label: "Venue Name", type: "text" }} value={draft.venueName} onChange={(v) => set("venueName", v)} />
        <Field field={{ label: "Venue Address", type: "text" }} value={draft.venueAddress} onChange={(v) => set("venueAddress", v)} />
      </div>
      <SaveBar onSave={save} saving={saving} />
    </div>
  );
};

const JourneyEditor = ({ content, refresh }) => {
  const { draft, set, save, saving } = useSection("journey", content, refresh);
  if (!draft) return null;
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field field={{ label: "Kicker", type: "text" }} value={draft.kicker} onChange={(v) => set("kicker", v)} />
        <Field field={{ label: "Title", type: "text" }} value={draft.title} onChange={(v) => set("title", v)} />
      </div>
      <ListField
        label="Chapters"
        items={draft.chapters}
        onChange={(v) => set("chapters", v)}
        addLabel="Add chapter"
        newItem={{ id: `chapter-${Date.now()}`, number: "", title: "", imageUrl: "", bodyHtml: "" }}
        schema={[
          { key: "number", label: "Number (e.g. 01)", type: "text" },
          { key: "id", label: "Anchor ID (for menu links)", type: "text" },
          { key: "title", label: "Title", type: "text" },
          { key: "imageUrl", label: "Image", type: "image", category: "journey" },
          { key: "bodyHtml", label: "Story", type: "rich" },
        ]}
      />
      <SaveBar onSave={save} saving={saving} />
    </div>
  );
};

const FamilyEditor = ({ content, refresh }) => {
  const { draft, set, save, saving } = useSection("family", content, refresh);
  if (!draft) return null;
  const personSchema = [
    { key: "name", label: "Name", type: "text" },
    { key: "role", label: "Role", type: "text" },
    { key: "imageUrl", label: "Photo", type: "image", category: "family" },
    { key: "bio", label: "Short bio", type: "textarea" },
  ];
  const m = draft.memorial || {};
  const setMem = (k, v) => set("memorial", { ...m, [k]: v });
  return (
    <div className="space-y-8">
      <Field field={{ label: "Intro", type: "textarea" }} value={draft.intro} onChange={(v) => set("intro", v)} />
      <ListField label="Our Kids" items={draft.kids} onChange={(v) => set("kids", v)} addLabel="Add" newItem={{ name: "", role: "", imageUrl: "", bio: "" }} schema={personSchema} reorderable />
      <ListField label="Siblings" items={draft.siblings} onChange={(v) => set("siblings", v)} addLabel="Add" newItem={{ name: "", role: "", imageUrl: "", bio: "" }} schema={personSchema} reorderable />
      <ListField label="Living Parents" items={draft.livingParents} onChange={(v) => set("livingParents", v)} addLabel="Add" newItem={{ name: "", role: "", imageUrl: "", bio: "" }} schema={personSchema} reorderable />
      <div className="border-t border-border pt-6 space-y-5">
        <h3 className="font-serif text-xl text-navy">Memorial Section</h3>
        <Field field={{ label: "Title", type: "text" }} value={m.title} onChange={(v) => setMem("title", v)} />
        <Field field={{ label: "Tribute Text (optional intro)", type: "rich" }} value={m.bodyHtml} onChange={(v) => setMem("bodyHtml", v)} />
        <ListField
          label="People Remembered"
          items={m.people}
          onChange={(v) => setMem("people", v)}
          addLabel="Add person"
          newItem={{ name: "", role: "", imageUrl: "", bio: "" }}
          schema={personSchema}
          reorderable
        />
      </div>
      <SaveBar onSave={save} saving={saving} />
    </div>
  );
};

const RsvpEditor = ({ content, refresh }) => {
  const { draft, set, save, saving } = useSection("rsvp", content, refresh);
  if (!draft) return null;
  return (
    <div className="space-y-6">
      <ImageField label="Hero Image" value={draft.heroImageUrl} category="hero" onChange={(v) => set("heroImageUrl", v)} />
      <Field field={{ label: "Title", type: "text" }} value={draft.title} onChange={(v) => set("title", v)} />
      <Field field={{ label: "Intro Text", type: "rich" }} value={draft.introHtml} onChange={(v) => set("introHtml", v)} />
      <SaveBar onSave={save} saving={saving} />
    </div>
  );
};

const SettingsEditor = ({ content, refresh }) => {
  const { draft, set, save, saving } = useSection("settings", content, refresh);
  if (!draft) return null;
  return (
    <div className="space-y-5 max-w-xl">
      <Field field={{ label: "Couple Names", type: "text" }} value={draft.coupleNames} onChange={(v) => set("coupleNames", v)} />
      <Field field={{ label: "Footer Text", type: "textarea" }} value={draft.footerText} onChange={(v) => set("footerText", v)} />
      <Field field={{ label: "Instagram Handle", type: "text" }} value={draft.instagramHandle} onChange={(v) => set("instagramHandle", v)} />
      <Field field={{ label: "Hashtag", type: "text" }} value={draft.hashtag} onChange={(v) => set("hashtag", v)} />
      <SaveBar onSave={save} saving={saving} />
    </div>
  );
};

const NavEditor = ({ content, refresh }) => {
  const { draft, set, save, saving } = useSection("nav", content, refresh);
  if (!draft) return null;
  const items = draft.items || [];
  const updateItem = (i, key, val) => set("items", items.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const updateChildren = (i, children) => updateItem(i, "children", children);
  return (
    <div className="space-y-6">
      <p className="text-navy/60 text-sm">Manage the top navigation and Level 2 sub-menu items. Use anchor links like <code>/journey#chapter-1</code> to smooth-scroll to a section.</p>
      {items.map((item, i) => (
        <div key={i} className="bg-offwhite/60 border border-border rounded-lg p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><span className={labelCls}>Label</span><input className={inputCls} value={item.label} onChange={(e) => updateItem(i, "label", e.target.value)} /></div>
            <div><span className={labelCls}>Path</span><input className={inputCls} value={item.path} onChange={(e) => updateItem(i, "path", e.target.value)} /></div>
          </div>
          <ListField
            label="Sub-menu (Level 2)"
            items={item.children || []}
            onChange={(v) => updateChildren(i, v)}
            addLabel="Add sub-item"
            newItem={{ label: "", path: "" }}
            schema={[{ key: "label", label: "Label", type: "text" }, { key: "path", label: "Path", type: "text" }]}
          />
        </div>
      ))}
      <SaveBar onSave={save} saving={saving} />
    </div>
  );
};

const PhotosManager = () => {
  const [images, setImages] = useState([]);
  const [category, setCategory] = useState("details");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/images");
    setImages(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Photo added to gallery");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const del = async (id) => {
    await api.delete(`/images/${id}`);
    await load();
    toast.success("Photo removed");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4 bg-offwhite/60 border border-border rounded-lg p-4">
        <div>
          <span className={labelCls}>Gallery</span>
          <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="photo-category">
            <option value="details">Wedding Details</option>
            <option value="journey">Our Journey</option>
            <option value="general">General</option>
          </select>
        </div>
        <label className="inline-flex items-center gap-2 rounded-full bg-navy text-white text-xs uppercase tracking-[0.14em] px-6 py-3 hover:bg-dusty transition-colors cursor-pointer" data-testid="photo-upload-btn">
          {busy ? "Uploading..." : "Upload Photo"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
        </label>
      </div>

      {images.length === 0 ? (
        <p className="text-navy/50 text-sm">No photos uploaded yet. Uploaded photos replace the sample gallery images automatically.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {images.map((img) => (
            <div key={img.id} className="relative group rounded-lg overflow-hidden border border-border">
              <img src={resolveImage(img.url)} alt={img.original_filename} className="w-full h-36 object-cover" />
              <span className="absolute top-2 left-2 bg-white/90 text-navy text-[10px] uppercase tracking-wide px-2 py-0.5 rounded">{img.category}</span>
              <button onClick={() => del(img.id)} className="absolute top-2 right-2 bg-white/90 text-destructive p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Delete">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Submissions = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/rsvp").then(({ data }) => setRows(data)); }, []);
  const attending = rows.filter((r) => r.attending === "yes");
  const totalGuests = attending.reduce((s, r) => s + (r.number_attending || 0), 0);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        {[["Responses", rows.length], ["Attending", attending.length], ["Total Guests", totalGuests]].map(([l, v]) => (
          <div key={l} className="bg-offwhite/60 border border-border rounded-lg p-5">
            <div className="font-serif text-3xl text-navy">{v}</div>
            <div className="uppercase tracking-[0.14em] text-[11px] text-navy/50 mt-1">{l}</div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-sm" data-testid="submissions-table">
          <thead className="bg-offwhite text-navy/60 uppercase tracking-[0.12em] text-[11px]">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Attending</th>
              <th className="text-left px-4 py-3">Guests</th>
              <th className="text-left px-4 py-3">Names</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border">
                <td className="px-4 py-3 text-navy font-medium">{r.full_name}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${r.attending === "yes" ? "bg-dusty/15 text-dusty" : "bg-secondary/50 text-navy/60"}`}>
                    {r.attending === "yes" ? "Yes" : "No"}
                  </span>
                </td>
                <td className="px-4 py-3 text-navy/70">{r.number_attending || "—"}</td>
                <td className="px-4 py-3 text-navy/70">{r.guest_names || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-navy/40">No RSVPs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { isAuthed, logout } = useAdmin();
  const { content, refresh } = useContent();
  const [tab, setTab] = useState("home");

  if (!isAuthed) return <Navigate to="/admin" replace />;

  const render = () => {
    switch (tab) {
      case "home": return <HomeEditor content={content} refresh={refresh} />;
      case "details": return <DetailsEditor content={content} refresh={refresh} />;
      case "journey": return <JourneyEditor content={content} refresh={refresh} />;
      case "family": return <FamilyEditor content={content} refresh={refresh} />;
      case "rsvp": return <RsvpEditor content={content} refresh={refresh} />;
      case "nav": return <NavEditor content={content} refresh={refresh} />;
      case "settings": return <SettingsEditor content={content} refresh={refresh} />;
      case "photos": return <PhotosManager />;
      case "submissions": return <Submissions />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-offwhite" data-testid="admin-dashboard">
      <header className="bg-navy text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-serif text-xl">George & Amy <span className="text-blush/70 text-sm font-sans">CMS</span></div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="inline-flex items-center gap-1.5 text-offwhite/80 hover:text-blush text-sm transition-colors">
              <ExternalLink size={15} /> View site
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-offwhite/80 hover:text-blush text-sm transition-colors" data-testid="admin-logout">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-left whitespace-nowrap rounded-md px-4 py-2.5 text-sm transition-colors ${
                tab === t.id ? "bg-navy text-white" : "text-navy/70 hover:bg-white"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <main className="bg-white rounded-xl border border-border p-6 md:p-10 min-h-[60vh]">
          <h2 className="font-serif text-3xl text-navy mb-8 flex items-center gap-2">
            <Home size={20} className="text-dusty" /> {TABS.find((t) => t.id === tab)?.label}
          </h2>
          {content ? render() : <p className="text-navy/50">Loading...</p>}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
